import type {
	DragControllerConfig,
	DragMovementParams,
	UseDragToBottomReturn
} from './types';

import { useDrag } from '@vueuse/gesture';
import { useMotionProperties, useSpring } from '@vueuse/motion';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import {
	HEADER_OFFSET_CONFIG,
	MOTION_PROPERTIES_CONFIG,
	SPRING_CONFIG,
	TRANSITION_CONFIG
} from './constants';
import { DragState, MenuLevel } from './types';
import { useDragCalculations } from './useDragCalculations';
import { useDragState } from './useDragState';
import { useMenuPositioning } from './useMenuPositioning';
import { useScrollMomentum } from './useScrollMomentum';

export function useDragToBottom(
	config: DragControllerConfig
): UseDragToBottomReturn {
	const { menuFixedLevels, scrollContainer, disabled } = config;

	// Element ref for dragging
	const dragRef = ref<HTMLElement | null>(null);

	// Initialize sub-composables
	const dragStateController = useDragState(config);
	const menuPositioning = useMenuPositioning(menuFixedLevels);
	const dragCalculations = useDragCalculations(menuFixedLevels, dragRef);
	const scrollMomentum = useScrollMomentum(
		scrollContainer,
		dragStateController.headerDragOffsetY,
		dragStateController.isScreenBelowBreakpoint
	);

	// Event bus for user dragging
	const userDragging = useEventBus<boolean>('property-listing:user-dragging');

	// Transition state management
	const isTransitioning = ref(false);
	let transitionTimeout: NodeJS.Timeout | null = null;

	// Motion properties
	const { motionProperties } = useMotionProperties(
		dragRef,
		MOTION_PROPERTIES_CONFIG
	);
	const { set } = useSpring(motionProperties as any, SPRING_CONFIG);

	// Function to set transitioning state with automatic reset
	function setTransitioning(transitioning: boolean): void {
		isTransitioning.value = transitioning;

		if (transitioning) {
			dragStateController.transitionTo(DragState.TRANSITIONING);

			// Clear any existing timeout
			if (transitionTimeout) {
				clearTimeout(transitionTimeout);
			}

			// Automatically reset transitioning state after animation duration
			transitionTimeout = setTimeout(() => {
				isTransitioning.value = false;
				if (
					dragStateController.currentState.value === DragState.TRANSITIONING
				) {
					dragStateController.transitionTo(DragState.IDLE);
				}
			}, SPRING_CONFIG.duration + 100); // Add small buffer to ensure animation completes
		} else {
			if (transitionTimeout) {
				clearTimeout(transitionTimeout);
				transitionTimeout = null;
			}
			if (dragStateController.currentState.value === DragState.TRANSITIONING) {
				dragStateController.transitionTo(DragState.IDLE);
			}
		}
	}

	// Watch for menu level changes based on menuFixedLevels updates
	watch(
		menuFixedLevels,
		(newLevels, oldLevels) => {
			if (
				dragStateController.menuLevel.value > MenuLevel.CLOSED &&
				dragStateController.isScreenBelowBreakpoint.value &&
				!dragStateController.isDragging.value
			) {
				const currentLevel = dragStateController.menuLevel.value;
				const oldHeight = oldLevels?.[currentLevel as keyof typeof oldLevels];
				const newHeight = newLevels[currentLevel as keyof typeof newLevels];

				if (oldHeight !== newHeight) {
					setMenuPosition(currentLevel, 0);
				}
			}
		},
		{ deep: true }
	);

	// Watch for screen size changes to reset visual position when changing from mobile to desktop
	watch(
		() => dragStateController.isScreenBelowBreakpoint.value,
		(isNowMobile, wasMobile) => {
			// Reset visual position when changing from mobile to desktop
			if (wasMobile && !isNowMobile) {
				set({ y: 0 });
				dragCalculations.resetDragPosition();
			}
		}
	);

	// Main drag handler
	const dragHandler = ({
		movement: [_, y],
		dragging,
		initial: [__, initialY]
	}: DragMovementParams) => {
		try {
			// Skip if drag is disabled
			if (disabled?.value) return;

			// Skip if not on mobile/tablet (screens wider than Tailwind's lg)
			if (!dragStateController.isScreenBelowBreakpoint.value) return;

			userDragging.emit(dragging);
			dragStateController.setDragging(dragging);

			// Stop momentum scroll if a new drag starts
			if (dragging && scrollMomentum.momentumState.isActive) {
				scrollMomentum.stopMomentumScroll();
				// Force drag variables to reset to current position
				dragCalculations.resetDragPosition();
			}

			// Handle small movements as clicks
			if (!dragging && dragCalculations.isSmallMovement(y)) {
				moveBackOneLevel();
				return;
			}

			// Initialize drag variables if needed
			dragCalculations.initializeDragWhenNeeded(dragging, initialY);

			const isDraggingUp = y < 0;
			const rawMovement = dragCalculations.calculateRawMovement(
				y,
				isDraggingUp
			);
			const actualMovement = dragCalculations.calculateActualMovement(
				isDraggingUp,
				rawMovement
			);

			// Check if we should handle scroll instead of drag
			if (
				scrollMomentum.shouldHandleScrollInsteadOfDrag(
					isDraggingUp,
					actualMovement,
					dragStateController.menuLevel.value
				)
			) {
				handleScrollMode(isDraggingUp, rawMovement, dragging, actualMovement);
				return;
			} else {
				// Reinitialize drag if coming out of scroll mode
				dragCalculations.reinitializeDragIfNeeded(
					dragging,
					dragStateController.currentY.value
				);
			}

			// Calculate screen bottom level for menu level determination
			const screenBottomLevel = actualMovement / window.innerHeight;

			// Update menu level based on position
			const newMenuLevel = menuPositioning.updateMenuLevel(actualMovement);
			dragStateController.setMenuLevel(newMenuLevel);

			// Handle drag release - snap to nearest level
			if (!dragging) {
				// Set transitioning state immediately to block any interactions during animation
				setTransitioning(true);
				setMenuPosition(dragStateController.menuLevel.value, 0);
				dragCalculations.resetDragPosition();

				// Apply header control based on final menu position
				applyHeaderControlAfterSnap();
				return;
			}

			// Apply drag limits and return if limit reached
			if (
				dragCalculations.applyDragLimits(
					actualMovement,
					isDraggingUp,
					dragStateController.isDragging.value
				)
			) {
				updateDragPositionWithLimits(actualMovement, isDraggingUp);
				return;
			}

			// Update drag position during active dragging
			updateDragPosition(actualMovement);

			// Allow header to be controlled by menu position when not scrolling
			dragStateController.setHeaderControlledByScroll(false);
		} catch (error) {
			// Error handling without console output
		}
	};

	function handleScrollMode(
		isDraggingUp: boolean,
		rawMovement: number,
		dragging: boolean,
		actualMovement: number
	): void {
		dragStateController.setScrollMode(true);

		if (isDraggingUp) {
			// Force div to exact top position when upward scroll begins
			if (dragStateController.currentY.value > 0) {
				set({ y: 0, cursor: dragging ? 'grabbing' : 'grab' });
				dragStateController.setCurrentY(0);
			}
			scrollMomentum.handleUpwardScroll(rawMovement);
		} else {
			scrollMomentum.handleDownwardScroll(rawMovement);

			// Keep drag position unchanged during scroll
			set({
				y: dragStateController.currentY.value,
				cursor: dragging ? 'grabbing' : 'grab'
			});
		}

		// Start momentum scroll when drag ends during scroll
		if (!dragging && dragStateController.isInScrollMode.value) {
			scrollMomentum.startMomentumScroll();
			return;
		}

		// Reset scroll mode when drag ends
		if (!dragging) {
			dragCalculations.resetDragPosition();
		}
	}

	function updateDragPositionWithLimits(
		actualMovement: number,
		isDraggingUp: boolean
	): void {
		if (isDraggingUp && actualMovement <= 0) {
			set({
				y: 0,
				cursor: dragStateController.isDragging.value ? 'grabbing' : 'grab'
			});
			dragStateController.setCurrentY(0);
			// Sync header with drag movement
			dragStateController.updateHeaderOffsetForDrag();
		} else if (!isDraggingUp) {
			const maxLimit = menuPositioning.getMaxDragLimit();
			set({
				y: maxLimit,
				cursor: dragStateController.isDragging.value ? 'grabbing' : 'grab'
			});
			dragStateController.setCurrentY(maxLimit);
			// Sync header with drag movement
			dragStateController.updateHeaderOffsetForDrag();
		}
	}

	function updateDragPosition(actualMovement: number): void {
		// Force exactly 0 when within 1 pixel to prevent visual gaps
		const exactMovement = actualMovement <= 1 ? 0 : actualMovement;

		set({
			y: exactMovement,
			cursor: dragStateController.isDragging.value ? 'grabbing' : 'grab'
		});
		dragStateController.setCurrentY(exactMovement);
		// Sync header with drag movement in real-time
		dragStateController.updateHeaderOffsetForDrag();
	}

	function applyHeaderControlAfterSnap(): void {
		nextTick(() => {
			if (dragStateController.isScreenBelowBreakpoint.value) {
				// Only apply header control if the menu is actually moved from initial position
				// This prevents the header from starting hidden on page load
				const currentY = dragStateController.currentY.value;
				const halfOpenPosition = menuPositioning.getPositionForLevel(
					MenuLevel.HALF_OPEN
				);

				if (currentY > HEADER_OFFSET_CONFIG.MIN_OFFSET) {
					// Calculate header offset based on progress towards HALF_OPEN level
					// Header should be fully hidden when reaching level 1 (HALF_OPEN)
					const progressToHalfOpen = Math.min(1, currentY / halfOpenPosition);
					const normalizedValue =
						progressToHalfOpen * HEADER_OFFSET_CONFIG.MOBILE_HEIGHT;
					dragStateController.headerDragOffsetY.value = normalizedValue;
				} else {
					// Ensure header is visible when at initial position
					dragStateController.headerDragOffsetY.value =
						HEADER_OFFSET_CONFIG.MIN_OFFSET;
				}
			}
		});
	}

	// Menu position control functions
	function setMenuPosition(level: number, movement: number): void {
		const position = menuPositioning.getPositionForLevel(level as MenuLevel);
		set({ y: position });
		dragStateController.setCurrentY(position);

		// Update header when not actively dragging (e.g., programmatic moves, snapping)
		if (
			!dragStateController.isDragging.value &&
			dragStateController.isScreenBelowBreakpoint.value
		) {
			// Only update header offset if position is greater than 0
			// This prevents the header from being hidden when at initial position
			if (position > HEADER_OFFSET_CONFIG.MIN_OFFSET) {
				const halfOpenPosition = menuPositioning.getPositionForLevel(
					MenuLevel.HALF_OPEN
				);
				// Calculate header offset based on progress towards HALF_OPEN level
				const progressToHalfOpen = Math.min(1, position / halfOpenPosition);
				const normalizedValue =
					progressToHalfOpen * HEADER_OFFSET_CONFIG.MOBILE_HEIGHT;
				dragStateController.headerDragOffsetY.value = normalizedValue;
			} else {
				// Ensure header is visible when at initial position
				dragStateController.headerDragOffsetY.value =
					HEADER_OFFSET_CONFIG.MIN_OFFSET;
			}
		}
	}

	async function moveBackToInitialPosition(): Promise<void> {
		setTransitioning(true);
		const newY = HEADER_OFFSET_CONFIG.MIN_OFFSET;
		set({ y: newY });
		await nextTick();
		dragStateController.setCurrentY(newY);
		dragStateController.setMenuLevel(MenuLevel.CLOSED);

		// Reset header when moving to initial position
		if (dragStateController.isScreenBelowBreakpoint.value) {
			dragStateController.headerDragOffsetY.value =
				HEADER_OFFSET_CONFIG.MIN_OFFSET;
		}
	}

	async function moveBackOneLevel(): Promise<void> {
		if (dragStateController.menuLevel.value === MenuLevel.FULLY_OPEN) {
			setTransitioning(true);
			const newY = menuPositioning.getPositionForLevel(MenuLevel.HALF_OPEN);
			set({ y: newY });
			await nextTick();
			dragStateController.setCurrentY(newY);
			dragStateController.setMenuLevel(MenuLevel.HALF_OPEN);

			// Update header for new position - only if position is greater than 0
			if (dragStateController.isScreenBelowBreakpoint.value) {
				if (newY > HEADER_OFFSET_CONFIG.MIN_OFFSET) {
					const halfOpenPosition = menuPositioning.getPositionForLevel(
						MenuLevel.HALF_OPEN
					);
					// When at HALF_OPEN level, header should be fully hidden
					const progressToHalfOpen = Math.min(1, newY / halfOpenPosition);
					const normalizedValue =
						progressToHalfOpen * HEADER_OFFSET_CONFIG.MOBILE_HEIGHT;
					dragStateController.headerDragOffsetY.value = normalizedValue;
				} else {
					dragStateController.headerDragOffsetY.value =
						HEADER_OFFSET_CONFIG.MIN_OFFSET;
				}
			}
		} else if (dragStateController.menuLevel.value === MenuLevel.HALF_OPEN) {
			moveBackToInitialPosition();
		}
	}

	async function toggleToClosedWhenOpen(): Promise<void> {
		if (!dragStateController.isScreenBelowBreakpoint.value) return;

		if (dragStateController.menuLevel.value === MenuLevel.CLOSED) {
			const newY = menuPositioning.getPositionForLevel(MenuLevel.FULLY_OPEN);
			set({ y: newY });

			dragStateController.setCurrentY(newY);
			dragStateController.setMenuLevel(MenuLevel.FULLY_OPEN);

			// Re-enable drag after opening the menu
			dragStateController.setDragging(false);
			dragCalculations.resetDragPosition();
			userDragging.emit(false);

			// Update header for new position - header should be hidden at FULLY_OPEN
			const halfOpenPosition = menuPositioning.getPositionForLevel(
				MenuLevel.HALF_OPEN
			);
			const progressToHalfOpen = Math.min(1, newY / halfOpenPosition);
			const normalizedValue =
				progressToHalfOpen * HEADER_OFFSET_CONFIG.MOBILE_HEIGHT;
			dragStateController.headerDragOffsetY.value = normalizedValue;
		}
	}

	async function toggleToHalfOpenWhenClosed(): Promise<void> {
		if (!dragStateController.isScreenBelowBreakpoint.value) return;

		if (dragStateController.menuLevel.value === MenuLevel.FULLY_OPEN) {
			const newY = menuPositioning.getPositionForLevel(MenuLevel.HALF_OPEN);
			set({ y: newY });

			dragStateController.setCurrentY(newY);
			dragStateController.setMenuLevel(MenuLevel.HALF_OPEN);

			dragStateController.setDragging(false);
			dragCalculations.resetDragPosition();
			userDragging.emit(false);

			// Update header for new position - header should be fully hidden at HALF_OPEN
			const halfOpenPosition = menuPositioning.getPositionForLevel(
				MenuLevel.HALF_OPEN
			);
			const progressToHalfOpen = Math.min(1, newY / halfOpenPosition);
			const normalizedValue =
				progressToHalfOpen * HEADER_OFFSET_CONFIG.MOBILE_HEIGHT;
			dragStateController.headerDragOffsetY.value = normalizedValue;
		}
	}

	const onTouchMove = (e: TouchEvent) => {
		if (dragStateController.isScreenBelowBreakpoint.value) {
			e.preventDefault();
		}
	};

	// Lifecycle hooks
	onMounted(() => {
		dragStateController.checkScreenSize();
		window.addEventListener('resize', dragStateController.checkScreenSize);

		// Prevent default touch behavior to avoid scrolling issues
		if (dragRef.value) {
			dragRef.value.addEventListener('touchmove', onTouchMove, {
				passive: false
			});
		}
	});

	onUnmounted(() => {
		window.removeEventListener('resize', dragStateController.checkScreenSize);
		if (dragRef.value) {
			dragRef.value.removeEventListener('touchmove', onTouchMove);
		}

		scrollMomentum.stopMomentumScroll();

		// Clean up transition timeout
		if (transitionTimeout) {
			clearTimeout(transitionTimeout);
			transitionTimeout = null;
		}
	});

	// Initialize drag gesture
	useDrag(dragHandler, {
		domTarget: dragRef,
		eventOptions: { passive: false },
		filterTaps: true,
		pointer: { touch: true },
		preventDefault: true,
		rubberband: true,
		axis: 'y'
	});

	return {
		dragRef,
		shouldFixOnBottom: dragStateController.shouldFixOnBottom,
		isDragging: dragStateController.isDragging,
		menuBottomLevel: dragStateController.menuLevel,
		currentY: dragStateController.currentY,
		dragHandler,
		moveBackOneLevel,
		moveBackToInitialPosition,
		toggleToClosedWhenOpen,
		toggleToHalfOpenWhenClosed,
		setMenuPosition,
		MENU_LEVELS: MenuLevel,
		isTransitioning
	};
}
